// Optimized Redux-Saga for Candidate Management
import React from "react";
import { all, takeEvery, takeLeading, put, call } from "redux-saga/effects";
import { toast } from "react-toastify";
import { getTenantId, Toast } from "../../utils/helper";
import * as TYPES from "../action/types";
import * as ACTIONS from "../action";
import INTERVIEW from "../../utils/api/interview";
import POSITION from "../../utils/api/candidate";

function* showToastError(message) {
  if (
    message &&
    message !== "Your session has been expired, please login again!"
  ) {
    yield toast.error(<Toast msg={message} />, {
      autoClose: 5000,
      toastId: message,
    });
  }
}

function* compareJDCVRequest({ payload }) {
  const { callback, onFailure, jd, cv, incbotInterviewId } = payload || {};
  try {
    const formData = new FormData();
    formData.append("jd", jd);
    formData.append("cv", cv);
    formData.append("incscreenInterviewId", incbotInterviewId);
    const tenantId = getTenantId();
    const res = yield INTERVIEW.post("incbot/compareJDToCV", formData, {
      headers: {
        "content-type": "multipart/form-data",
        ...(tenantId && { "x-tenant-id": tenantId }),
      },
    });
    if ([200, 201].includes(res.status)) {
      yield put(ACTIONS.compareJDCVSuccess(res.data?.result ?? res.data));
      if (typeof callback === "function") {
        yield call(callback, res.data?.result ?? res.data);
      }
    } else {
      yield put(ACTIONS.compareJDCVFailure());
      if (typeof onFailure === "function") {
        yield call(onFailure);
      }
    }
  } catch (e) {
    yield put(ACTIONS.compareJDCVFailure());
    if (typeof onFailure === "function") {
      yield call(onFailure);
    }
    yield call(showToastError, e?.response?.data?.message);
  }
}

function* fetchResumeScoreRequest({ payload }) {
  const { callback, onFailure, ...requestPayload } = payload || {};
  try {
    const res = yield POSITION.post(
      "incscreen/interviews/fetchResumeScore",
      requestPayload,
    );
    if ([200, 201].includes(res.status)) {
      yield put(ACTIONS.fetchResumeScoreSuccess(res.data.result));
      if (typeof callback === "function") {
        yield call(callback, res.data.result);
      }
    } else {
      yield put(ACTIONS.fetchResumeScoreFailure());
      if (typeof onFailure === "function") {
        yield call(onFailure);
      }
    }
  } catch (e) {
    yield put(ACTIONS.fetchResumeScoreFailure());
    if (typeof onFailure === "function") {
      yield call(onFailure);
    }
  }
}

function* manualInviteRequest({ payload }) {
  try {
    const res = yield POSITION.patch(
      `incscreen/interviews/manualInvite/${payload.interviewId}`,
    );
    if ([200, 201].includes(res.status)) {
      yield put(ACTIONS.manualInviteSuccess());
      yield call(payload.callback, res.data.result);
    }
  } catch (e) {
    yield put(ACTIONS.manualInviteFailure());
    yield call(showToastError, e?.response?.data?.message);
  }
}

function* updateStatusRequest({ payload }) {
  try {
    const res = yield POSITION.patch(
      `incscreen/interviews/updateStatus/${payload.interviewId}/${payload.status}`,
    );
    if ([200, 201].includes(res.status)) {
      yield put(ACTIONS.updateStatusSuccess());
      yield call(payload.callback, res.data.result);
    }
  } catch (e) {
    yield put(ACTIONS.updateStatusFailure());
    yield call(showToastError, e?.response?.data?.message);
  }
}

function* getInterviewRequest({ payload }) {
  try {
    const res = yield POSITION.get(
      `incscreen/interviews/getRecording/${payload.interviewId}/${payload.roomId}`,
    );
    if ([200, 201].includes(res.status)) {
      yield put(ACTIONS.getInterviewSuccess(res.data.result));
    }
  } catch (e) {
    yield put(ACTIONS.getInterviewFailure());
    yield call(showToastError, e?.response?.data?.message);
  }
}

function* getConfigInfoRequest() {
  try {
    const res = yield POSITION.get("incscreen/interviews/getConfigInfo");
    if ([200, 201].includes(res.status)) {
      yield put(ACTIONS.getConfigInfoSuccess(res.data.result));
    }
  } catch (e) {
    yield put(ACTIONS.getConfigInfoFailure());
    yield call(showToastError, e?.response?.data?.message);
  }
}

function* getScheduleInfoRequest({ payload }) {
  try {
    const res = yield POSITION.get(
      `incscreen/interviews/getScheduleInfo/${payload.interviewId}/${payload.roomId}`,
    );
    if ([200, 201].includes(res.status)) {
      yield put(ACTIONS.getScheduleInfoSuccess(res.data.result));
      yield call(payload.callback, res.data.result?.status);
    }
  } catch (e) {
    yield put(ACTIONS.getScheduleInfoFailure());
    yield call(showToastError, e?.response?.data?.message);
  }
}

function* getCandidatesByIdRequest({ payload }) {
  try {
    const res = yield POSITION.get(
      `incscreen/interviews/getCandidates/${payload.interviewId}`,
    );
    if ([200, 201].includes(res.status)) {
      yield put(ACTIONS.getCandidatesByIdSuccess(res.data.result));
      if (payload.callback) {
        yield call(payload.callback, res.data.result);
      }
    }
  } catch (e) {
    yield put(ACTIONS.getCandidatesByIdFailure());
    if (payload.failure) {
      yield call(payload.failure);
    }
    yield call(showToastError, e?.response?.data?.message);
  }
}

function* postScheduleInfoRequest({ payload }) {
  try {
    const res = yield POSITION.post(
      "incscreen/interviews/postScheduleInfo",
      payload,
    );
    if ([200, 201].includes(res.status)) {
      yield put(ACTIONS.postScheduleInfoSuccess(res.data.result));
      yield call(payload.callback);
    }
  } catch (e) {
    yield put(ACTIONS.postScheduleInfoFailure());
    yield call(showToastError, e?.response?.data?.message);
  }
}

function* postScheduleWhatsappRequest({ payload }) {
  try {
    const res = yield POSITION.post(
      "incscreen/interviews/postScheduleWhatsapp",
      payload,
    );
    if ([200, 201].includes(res.status)) {
      yield put(ACTIONS.postScheduleWhatsappSuccess(res.data.result));
    }
  } catch (e) {
    yield put(ACTIONS.postScheduleWhatsappFailure());
    yield call(showToastError, e?.response?.data?.message);
  }
}

function* getDropdownQuestionRequest({ payload }) {
  try {
    const res = yield POSITION.get(
      `incscreen/positions/getQuestionDropDown/${payload?.position}`,
    );
    if ([200, 201].includes(res.status)) {
      yield put(ACTIONS.getDropdownQuestionSuccess(res.data.result));
    }
  } catch (e) {
    yield put(ACTIONS.getDropdownQuestionFailure());
    yield call(showToastError, e?.response?.data?.message);
  }
}

function* getDropdownPositionRequest() {
  try {
    const res = yield POSITION.get(`incscreen/positions/getPositionDropDown`);
    if ([200, 201].includes(res.status)) {
      yield put(ACTIONS.getDropdownPositionSuccess(res.data.result));
    }
  } catch (e) {
    yield put(ACTIONS.getDropdownPositionFailure());
  }
}

function* updateFinalOutcomeRequest({ payload }) {
  try {
    const res = yield POSITION.put(
      `incscreen/interviews/updateFinalOutCome/${payload.interviewId}`,
      { status: payload.status },
    );
    if ([200, 201].includes(res.status)) {
      yield put(ACTIONS.updateFinalOutcomeSuccess(res.data.result));
      if (payload.callback) {
        yield call(payload.callback, res.data.result);
      }
    }
  } catch (e) {
    yield put(ACTIONS.updateFinalOutcomeFailure());
    yield call(showToastError, e?.response?.data?.message);
  }
}

function* createCandidatesRequest({ payload }) {
  try {
    const { position, question } = payload.form;
    const formData = new FormData();
    if (payload?.quickcall) {
      formData.append("candidates", JSON.stringify(payload.candidates));
      formData.append("position", position);
      formData.append("question", question);
      formData.append("quickcall", true);
      if (payload?.form?.resume) {
        formData.append("resume", payload.form.resume);
      }
      if (payload.triggerCallCount !== undefined && payload.triggerCallDocId) {
        formData.append("triggerCallCount", payload.triggerCallCount);
        formData.append("triggerCallDocId", payload.triggerCallDocId);
      }
    } else {
      formData.append("position", position);
      formData.append("question", question);
      formData.append("candidates", JSON.stringify(payload.candidates));
      (payload.resume || []).forEach(
        (file) => file && formData.append("resume", file),
      );
    }
    const tenantId = getTenantId();
    const res = yield POSITION.post(
      `incscreen/interviews/createCandidates`,
      formData,
      {
        headers: {
          "content-type": "multipart/form-data",
          ...(tenantId && { "x-tenant-id": tenantId }),
        },
      },
    );

    if (res.status === 201) {
      yield put(ACTIONS.createCandidatesSuccess(res.data.result));
      yield call(payload.callback);
    }
  } catch (e) {
    yield call(payload.callbackFailure, e?.response?.data?.message);
    yield put(ACTIONS.createCandidatesFailure());
    yield call(showToastError, e?.response?.data?.message);
  }
}

function* getCandidateRequest({ payload }) {
  try {
    const {
      search,
      page,
      pageSize,
      filter = {},
      sort,
      callback,
      isCompletedTab = false,
    } = payload;

    const defaultStatus = "";

    const query = [
      page && pageSize && `page=${page}&pageSize=${pageSize}`,
      search && `search=${encodeURIComponent(search)}`,
      filter.from && `from=${filter.from}T00:00:00`,
      filter.to && `to=${filter.to}T23:59:59`,
      filter.qFrom && filter.qTo && `qFrom=${filter.qFrom}&qTo=${filter.qTo}`,
      (filter.status || defaultStatus) &&
        `status=${filter.status || defaultStatus}`,
      filter.createdBy && `createdBy=${filter.createdBy}`,
      filter.sort || sort ? `sort=${filter.sort || sort}` : null,
      isCompletedTab && `isCompletedTab=true`,
    ]
      .filter(Boolean)
      .join("&");

    const res = yield POSITION.get(
      `incscreen/interviews/getCandidates?${query}`,
    );

    if ([200, 201].includes(res.status)) {
      if (page === "all") {
        yield put(ACTIONS.getCandidateFailure());
        yield call(callback, res.data.result);
      } else {
        yield put(ACTIONS.getCandidateSuccess(res.data.result));
      }
    } else {
      yield put(ACTIONS.getCandidateFailure());
    }
  } catch (e) {
    yield put(ACTIONS.getCandidateFailure());
    yield call(showToastError, e?.response?.data?.message);
  }
}

function* postCandidateRequest({ payload }) {
  try {
    const {
      position,
      question,
      candidate,
      interviewId,
      replicateId,
      resume,
      checkResumeScore,
      resumeScore,
    } = payload.form;
    const formData = new FormData();
    formData.append("position", position);
    formData.append("question", question);
    formData.append("candidate", JSON.stringify(candidate));
    formData.append("cecKey", true);
    formData.append("checkResumeScore", checkResumeScore || false);
    if (resumeScore) formData.append("resumeScore", resumeScore || 0);
    if (interviewId) formData.append("interviewId", interviewId);
    if (replicateId) formData.append("replicateId", replicateId);
    if (resume instanceof File) {
      formData.append("resume", resume);
    }
    const tenantId = getTenantId();
    const res = yield POSITION.post(
      `incscreen/interviews/createAndUpdateCandidate`,
      formData,
      {
        headers: {
          "content-type": "multipart/form-data",
          ...(tenantId && { "x-tenant-id": tenantId }),
        },
      },
    );

    if (res.status === 201) {
      yield put(ACTIONS.createOrUpdateCandidateSuccess(res.data.result));
      yield call(payload.callback);
    }
  } catch (e) {
    yield put(ACTIONS.createOrUpdateCandidateFailure());
    yield call(showToastError, e?.response?.data?.message);
  }
}

function* cancelInterviewRequest({ payload }) {
  try {
    const res = yield POSITION.put(
      `incscreen/interviews/cancelInterview/${payload.interviewId}`,
      {
        reason: payload.reason,
      },
    );

    if ([200, 201].includes(res.status)) {
      yield put(ACTIONS.cancelInterviewSuccess(res.data.result));
      toast.success(<Toast msg={"Interview Canceled Successfully"} />, {
        autoClose: 5000,
        toastId: "Interview Canceled Successfully",
      });
      yield call(payload.callback);
    }
  } catch (e) {
    yield put(ACTIONS.cancelInterviewFailure());
    yield call(showToastError, e?.response?.data?.message);
  }
}

function* postBulkCandidateRequest({ payload }) {
  try {
    const {
      position,
      question,
      candidateTracker,
      resumeZip = "",
      confirm = 0,
    } = payload.form;
    const formData = new FormData();
    formData.append("position", position);
    formData.append("question", question);
    formData.append("confirm", confirm);
    formData.append("candidateTracker", candidateTracker);
    if (resumeZip) formData.append("resumeZip", resumeZip);
    const tenantId = getTenantId();

    const res = yield POSITION.post(
      `incscreen/interviews/createBulkCandidate`,
      formData,
      {
        headers: {
          "content-type": "multipart/form-data",
          ...(tenantId && { "x-tenant-id": tenantId }),
        },
      },
    );

    if ([200, 201].includes(res.status)) {
      const message = res.data.result?.error || res.data.result?.errors;
      if (!res.data.result?.status && message) {
        yield put(ACTIONS.createBulkCandidateFailure());
        toast.error(<Toast msg={message} />, {
          autoClose: 5000,
          toastId: message,
        });
      } else {
        yield put(ACTIONS.createBulkCandidateSuccess(res.data.result));
        yield call(payload.callback);
        if (payload?.successMessage) {
          toast.success(<Toast msg={payload?.successMessage} />, {
            autoClose: 5000,
            toastId: payload?.successMessage,
          });
        }
      }
    }
  } catch (e) {
    yield put(ACTIONS.createBulkCandidateFailure());
    yield call(showToastError, e?.response?.data?.message);
  }
}

function* checkCandidateBankExistsRequest({ payload }) {
  try {
    const { data, callback } = payload;
    const res = yield INTERVIEW.post(`incbot/candidateBank/checkDuplicate`, {
      bankId: data?.bankId,
      positionId: data?.position,
      questionId: data?.question,
    });

    if ([200, 201].includes(res.status)) {
      yield put(
        ACTIONS.checkCandidateBankExistsSuccess(res?.data?.result?.status),
      );
      yield call(callback, res?.data?.result?.status);
    }
  } catch (e) {
    yield put(ACTIONS.checkCandidateBankExistsFailure(false));
    yield call(showToastError, e?.response?.data?.message);
  }
}

function* getCandidateDetailsUsingResumeRequest(action) {
  try {
    const tenantId = getTenantId();
    let formData = new FormData();
    formData.append("cv", action.payload.resume);
    const res = yield INTERVIEW.post(
      `interviews/extractDataFromResume`,
      formData,
      {
        headers: {
          "content-type": "multipart/form-data",
          ...(tenantId && { "x-tenant-id": tenantId }),
        },
      },
    );
    if (res.status === 201 || res.status === 200) {
      yield put(ACTIONS.getCandidateDetailsUsingResumeSuccess(res.data));
      if (res.data.email_id || res.data.phone_number) {
        const message = "Candidate Details Fetched Successfully";
        toast.success(<Toast msg={message} />, {
          autoClose: 5000,
          toastId: message,
        });
      }
    }
  } catch (e) {
    yield put(ACTIONS.getCandidateDetailsUsingResumeFailure());
    yield call(showToastError, e?.response?.data?.message);
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(TYPES.COMPARE_JD_CV, compareJDCVRequest),
    takeEvery(TYPES.FETCH_RESUME_SCORE, fetchResumeScoreRequest),
    takeEvery(TYPES.MANUAL_INVITE, manualInviteRequest),
    takeEvery(TYPES.UPDATE_INTERVIEW_STATUS, updateStatusRequest),
    takeEvery(TYPES.GET_INTERVIEW, getInterviewRequest),
    takeEvery(TYPES.GET_CANDIDATE, getCandidateRequest),
    takeEvery(TYPES.POST_CANDIDATE, postCandidateRequest),
    takeEvery(TYPES.CANCEL_INTERVIEW, cancelInterviewRequest),
    takeEvery(TYPES.POST_BULK_CANDIDATE, postBulkCandidateRequest),
    takeEvery(TYPES.GET_DROPDOWN_QUESTION, getDropdownQuestionRequest),
    takeLeading(TYPES.GET_DROPDOWN_POSITION, getDropdownPositionRequest),
    takeEvery(
      TYPES.CHECK_CANDIDATE_BANK_EXISTS,
      checkCandidateBankExistsRequest,
    ),
    takeEvery(TYPES.CREATE_CANDIDATES, createCandidatesRequest),
    takeEvery(TYPES.UPDATE_FINAL_OUTCOME, updateFinalOutcomeRequest),
    takeEvery(
      TYPES.GET_CANDIDATE_DETAILS_USING_RESUME,
      getCandidateDetailsUsingResumeRequest,
    ),

    takeEvery(TYPES.GET_CONFIG_INFO, getConfigInfoRequest),
    takeEvery(TYPES.GET_SCHEDULE_INFO, getScheduleInfoRequest),
    takeEvery(TYPES.POST_SCHEDULE_INFO, postScheduleInfoRequest),
    takeEvery(TYPES.POST_SCHEDULE_WHATSAPP, postScheduleWhatsappRequest),
    takeEvery(TYPES.GET_CANDIDATES_BY_ID, getCandidatesByIdRequest),
  ]);
}
